"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";

import Fuse from "fuse.js";
import { useQuery } from "@tanstack/react-query";
import { LuInfo } from "react-icons/lu";
import { RiSearchLine } from "react-icons/ri";
import { TbCalendarEvent } from "react-icons/tb";

import { cn } from "~/utils/fontsConfig";
import { slugifyUser } from "~/utils/string";
import { useEventContextV2 } from "~/app/providers";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { Checkbox } from "~/components/shadcn/Checkbox";
import { Input } from "~/components/v4/shadcn/Input";
import { SpinnerV1 } from "~/components/themed/spinner";

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "~/components/v4/shadcn/Tooltip";
import {
    GetProximityAndFollowersInvites,
    getProximityInviteSuggestions,
} from "~/app/events/[slug]/invites/actions";
import { useInvites } from "../contexts/InvitesContext";
import { InviteWithId } from "../../actions";
import InvitedAttendees from "../InvitedAttendees";

export const NearbyGuests = ({
    initialData,
}: {
    initialData: GetProximityAndFollowersInvites;
}) => {
    const event = useEventContextV2();
    const hostId = event?.community?.id ?? event?.user?.id;
    const [searchTerm, setSearchTerm] = useState("");
    const { proximity, setProximity, setTotalCount } = useInvites();

    const { data: invitesData, isLoading } = useQuery(
        ["proximity-invites", hostId, event?.id],
        () =>
            getProximityInviteSuggestions({
                params: {
                    communitySlug: event?.community?.slug,
                    eventSlug: event?.slug,
                },
            }),
        {
            enabled: !!event?.location,
            initialData: {
                eligibleUsers: initialData?.eligibleUsers,
                inviteCount: initialData?.inviteCount,
            },
            refetchOnWindowFocus: false,
        },
    );

    useEffect(() => {
        if (invitesData?.eligibleUsers) {
            setTotalCount((prev) => ({
                ...prev,
                proximity: invitesData?.eligibleUsers.length,
            }));
        }
    }, [invitesData]);

    const suggestions = invitesData?.eligibleUsers ?? [];
    const invitesCount = invitesData?.inviteCount;

    const fuse = useMemo(
        () =>
            new Fuse(suggestions, {
                keys: ["name", "email"],
                threshold: 0.3,
                ignoreLocation: true,
                shouldSort: true,
            }),
        [suggestions],
    );

    const filteredSuggestions = useMemo(() => {
        if (!searchTerm.trim()) return suggestions;
        return fuse.search(searchTerm).map((result) => result.item);
    }, [searchTerm, fuse, suggestions]);

    const toggleNearbySelection = (user: InviteWithId) => {
        setProximity((prev) =>
            prev.some((invite) => invite.id === user.id)
                ? prev.filter((invite) => invite.id !== user.id)
                : [...prev, user],
        );
    };

    const toggleAllNearby = () => {
        if (proximity.length === suggestions.length) {
            setProximity([]);
        } else {
            setProximity(
                suggestions.map((suggestion) => ({
                    id: suggestion.id,
                    name: suggestion.name,
                    email: suggestion.email ?? "",
                    image: suggestion.image,
                })),
            );
        }
    };

    if (!event?.location) {
        return (
            <div className="flex flex-col gap-2">
                <div className="text-lg">Add Location to Invite Guests</div>

                <div className="text-sm text-gray-500">
                    Set the location for your event to unlock the ability to
                    invite nearby guests.
                </div>
            </div>
        );
    }

    if (isLoading) return <SpinnerV1 center />;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                    <span className="flex items-center gap-2 font-[500] text-white">
                        <span>Nearby Members</span>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <LuInfo className="h-4 w-4" />
                                </TooltipTrigger>
                                <TooltipContent className="bg-v3-N-400 text-center text-sm text-white">
                                    <div>
                                        Nearby members only available for events
                                    </div>
                                    <div>within a community.</div>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </span>
                    <div className="text-sm text-gray-500">
                        {invitesCount} already invited from guest(s)
                    </div>
                </div>

                <Input
                    placeholder="Search by name, location or email"
                    className="mb-2 mt-2 w-full rounded-[10px]"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    prefixComponent={<RiSearchLine className="h-4 w-4" />}
                />
                <InvitedAttendees
                    invitedAttendees={initialData.invitedAttendees}
                />

                <div className="ml-2 mt-2 flex items-center gap-2">
                    <Checkbox
                        className={cn(
                            "rounded-[4px] border-2",
                            "data-[state=checked]:border-none data-[state=checked]:bg-v3-clover",
                            "data-[state=checked]:text-white",
                        )}
                        checked={
                            proximity.length === suggestions.length &&
                            suggestions.length > 0
                        }
                        onCheckedChange={toggleAllNearby}
                    />
                    <span className="text-sm text-gray-400">
                        Select all {suggestions.length} nearby guests
                    </span>
                </div>

                {suggestions.length === 0 && (
                    <div className="flex items-center justify-center text-sm text-gray-500">
                        No nearby guests found.
                    </div>
                )}
            </div>

            {filteredSuggestions.length > 0 && (
                <div className="max-h-[450px] space-y-2 overflow-y-scroll pb-20 md:pb-10">
                    {filteredSuggestions.map((user) => (
                        <div
                            key={user.id}
                            className="flex items-center justify-between rounded-lg p-2"
                        >
                            <div className="flex items-center gap-4">
                                <Checkbox
                                    className={cn(
                                        "rounded-[4px] border-2",
                                        "data-[state=checked]:border-none data-[state=checked]:bg-v3-clover",
                                        "data-[state=checked]:text-white",
                                    )}
                                    checked={proximity.some(
                                        (invite) => invite.id === user.id,
                                    )}
                                    onCheckedChange={() =>
                                        toggleNearbySelection({
                                            id: user.id,
                                            name: user.name,
                                            email: user.email ?? "",
                                            image: user.image,
                                        })
                                    }
                                />
                                <Link
                                    href={`/profile/${slugifyUser(user)}`}
                                    target="_blank"
                                >
                                    <UserAvatar
                                        user={user}
                                        className="h-10 w-10"
                                    />
                                </Link>

                                <div className="flex flex-col gap-2">
                                    <span className="text-sm text-white">
                                        {user.name}
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <TbCalendarEvent className="h-4 w-4" />
                                        <span className="text-sm text-gray-400">
                                            {user.attendedEvents} events
                                            attended in {user.lastEventCity}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
